import React, { useState, useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import { useChatInputStore, useAuthStore } from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';
import ExplorePage, { ExploreSection } from '@/components/Pages/ExplorePage';
import VideoStyleCarousel from '@/components/VideoStyleCarousel';
import { CarouselNavButtons } from '@/components/ImageStyleCarousel';
import VideoModelsSection from '@/components/VideoModelsSection';
import VideoPromptGrid from '@/components/VideoPromptGrid';
import VideoGallery from '@/components/VideoGallery';
import ToolsByOutputTypeSection from '@/components/Tools/ToolsByOutputTypeSection';
import SectionViewAllLink from '@/components/Explore/SectionViewAllLink';
import AppDashboardFooter from '@/components/AppDashboardFooter';

const VideosPage = React.memo(() => {
  const router = useRouter();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [carouselScrollState, setCarouselScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
    showNavButtons: false,
  });
  const carouselRef = useRef(null);

  const initializeVideoGeneration = useChatInputStore(state => state.initializeVideoGeneration);
  const hasUser = useAuthStore(state => Boolean(state.user?.id));
  const requireAuth = useRequireAuth();

  const handleSubmit = useCallback(() => {
    if (!inputValue.trim()) return;
    // Gate before preparing pendingAutoSend — the auto-send handler on
    // `/app` would otherwise no-op for anonymous visitors and quietly
    // discard the prompt they just typed.
    const ran = requireAuth(() => true, {
      source: 'videos_search_submit',
      title: 'Sign in to make a video',
      subtitle: 'Video generations are billed to your account so we can apply your credits and save the result.',
      icon: 'video',
    });
    if (ran !== true) return;
    initializeVideoGeneration(inputValue.trim(), null, true);
    router.push('/app');
  }, [inputValue, initializeVideoGeneration, router, requireAuth]);

  const onRefresh = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, 500));
  }, []);

  return (
    <ExplorePage
      metaTitle="Videos"
      metaDescription="Create stunning AI-generated videos. Explore different styles, try various video generation models, and manage your video gallery."
      metaCanonical="/videos"
      searchPlaceholder={t('videos.inputPlaceholder')}
      searchIcon="video"
      searchValue={inputValue}
      onSearchChange={setInputValue}
      onSearchSubmit={handleSubmit}
      onRefresh={onRefresh}
      footer={<AppDashboardFooter fullViewportWidth />}
    >
      <ExploreSection
        title={t('videos.tryAStyle')}
        headerRight={
          carouselScrollState.showNavButtons ? (
            <CarouselNavButtons
              canScrollLeft={carouselScrollState.canScrollLeft}
              canScrollRight={carouselScrollState.canScrollRight}
              onScrollLeft={() => carouselRef.current?.scrollBy(-300)}
              onScrollRight={() => carouselRef.current?.scrollBy(300)}
            />
          ) : null
        }
      >
        <VideoStyleCarousel
          ref={carouselRef}
          onScrollStateChange={setCarouselScrollState}
        />
      </ExploreSection>

      {Platform.OS !== 'ios' && (
        <ExploreSection
          title={t('videos.tryOurModels')}
          headerRight={<SectionViewAllLink href="/models" label={t('common.viewAllModels')} />}
        >
          <VideoModelsSection />
        </ExploreSection>
      )}

      <ExploreSection
        title={t('videos.tryOurTools')}
        headerRight={<SectionViewAllLink href="/tools" label={t('common.viewAllTools')} />}
      >
        <ToolsByOutputTypeSection outputType="video" />
      </ExploreSection>

      {/* "Discover something new" prompt grid is hidden for logged-out visitors. */}
      {hasUser && ['web', 'macos'].includes(Platform.OS) && (
        <ExploreSection title={t('videos.discoverNew')}>
          <VideoPromptGrid />
        </ExploreSection>
      )}

      {/* Personal gallery is account-bound — hidden for logged-out visitors. */}
      {hasUser && (
        <ExploreSection title={t('videos.myVideos')}>
          <VideoGallery />
        </ExploreSection>
      )}
    </ExplorePage>
  );
});

function VideosRoute() {
  return <VideosPage />;
}

export default React.memo(VideosRoute);
